'use client';
import { Landscape_Description } from '@/components/ui/catalog/landscape/description';
import { Landscape_Display } from '../landscape/LandscapeDisplay';
import { Landscape_Immersive } from '../landscape/LandscapeImmersive';
import type {
  Award,
  LandscapeDisplay,
  LandscapeImmersive,
  LandscapeDescription,
  LandscapeChapterHero,
  LandscapeChapter,
  LandscapeProcess,
  LandscapeSketchbook,
  LandscapeDocumentary,
  LandscapeRenders,
  LandscapePress,
  LandscapeGallery,
  LandscapeExhibition,
  LandscapeEmbed
} from '@/types';
import { Landscape_ChapterHero } from '../landscape/LandscapeChapterHero';
import { Landscape_Chapter } from '../landscape/LandscapeChapter';
import { Landscape_Process } from '../landscape/LandscapeProcess';
import { Landscape_Sketchbook } from '../landscape/LandscapeSketchbook';
import { Common_HeroSection } from '../landscape/CommonHeroSection';
import { EntryInformation, readLayout, type EntrySpec } from '../landscape/EntryInformation';
import { Landscape_Documentary } from '../landscape/LandscapeDocumentary';
import { Landscape_Renders } from '../landscape/LandscapeRender';
import { Landscape_Gallery } from '../landscape/LandscapeGallery';
import { Landscape_Press } from '../landscape/LandscapePress';
import { getVideoOrFileData } from '@/lib/utils/media.utils';
import { Landscape_Exhibition } from '../landscape/LandscapeExhibition';
import { Landscape_Embed } from '../landscape/LandscapeEmbed';

interface AwardProps {
  detail: Award;
}

export default function AwardDetailPage({ detail }: AwardProps) {
  // Convert tags
  const tags: string[] = [];

  if (detail.tags && Array.isArray(detail.tags)) {
    detail.tags.forEach(tag => {
      if (typeof tag === 'number') {
        tags.push(String(tag));
      } else if (tag && typeof tag === 'object' && 'title' in tag) {
        tags.push(tag.title);
      }
    });
  }

  // What the entry knows about itself. Shown by the description block where the layout has
  // one, and by the page itself where it does not, so no entry is left as a bare title.
  const specs: EntrySpec[] = [];
  if (detail.category) {
    specs.push({
      label: 'Result',
      specifications: [{ value: detail.category }],
    });
  }

  // Status
  if (detail.status && typeof detail.status === 'object') {
    specs.push({
      label: 'Status',
      specifications: [{ value: detail.status.title }],
    });
  }

  // Type/Category
  if (detail.type && typeof detail.type === 'object') {
    specs.push({
      label: 'Type',
      specifications: [{ value: detail.type.title }],
    });
  }

  // Organization
  if (detail.organization) {
    specs.push({
      label: 'Presented By',
      specifications: [{ value: detail.organization }],
    });
  }

  // Prize
  if (detail.prize) {
    specs.push({
      label: 'Prize',
      specifications: [{ value: detail.prize }],
    });
  }

  // Year
  if (detail.year) {
    specs.push({
      label: 'Year',
      specifications: [{ value: String(detail.year) }],
    });
  }

  // Ceremony location
  if (detail.ceremony?.location) {
    const locationParts = [
      detail.ceremony.location,
      detail.ceremony.city,
      detail.ceremony.country
    ].filter(Boolean);
    if (locationParts.length > 0) {
      specs.push({
        label: 'Ceremony',
        specifications: [{ value: locationParts.join(', ') }],
      });
    }
  }

  // Award URL
  if (detail.awardUrl) {
    try {
      specs.push({
        label: 'Official Page',
        specifications: [
          {
            value: new URL(detail.awardUrl).hostname.replace(/^www\./, ''),
            url: detail.awardUrl,
            light: true,
          },
        ],
      });
    } catch {
      // Invalid URL, skip
    }
  }

  // Press Release File
  const pdfFile = getVideoOrFileData(detail.pressReleaseFile);
  if (pdfFile.url) {
    specs.push({
      label: 'Press Release',
      specifications: [
        {
          value: 'Download',
          url: pdfFile.url,
          light: true,
        },
      ],
    });
  }

  const typeTitle = detail.type && typeof detail.type === 'object' ? detail.type.title : null;
  const page = readLayout(detail.layout);

  const GetLayout = ({
    layout,
  }: {
    layout:
      | LandscapeDisplay
      | LandscapeImmersive
      | LandscapeDescription
      | LandscapeChapterHero
      | LandscapeChapter
      | LandscapeProcess
      | LandscapeSketchbook
      | LandscapeDocumentary
      | LandscapeRenders
      | LandscapeGallery
      | LandscapePress
      | LandscapeExhibition
      | LandscapeEmbed;
  }) => {
    switch (layout.blockType) {
      case 'LandscapeDisplay':
        return <Landscape_Display layout={layout} />;
      case 'LandscapeImmersive':
        return <Landscape_Immersive layout={layout} />;
      case 'LandscapeChapterHero':
        return <Landscape_ChapterHero layout={layout} />;
      case 'LandscapeChapter':
        return <Landscape_Chapter layout={layout} />;
      case 'LandscapeProcess':
        return <Landscape_Process layout={layout} />;
      case 'LandscapeSketchbook':
        return <Landscape_Sketchbook layout={layout} />;
      case 'LandscapeDocumentary':
        return <Landscape_Documentary layout={layout} />;
      case 'LandscapeRenders':
        return <Landscape_Renders layout={layout} />;
      case 'LandscapePress':
        return <Landscape_Press layout={layout} />;
      case 'LandscapeGallery':
        return <Landscape_Gallery layout={layout} />;
      case 'LandscapeExhibition':
        return <Landscape_Exhibition layout={layout} />;
      case 'LandscapeEmbed':
        return <Landscape_Embed layout={layout} />;
      case 'LandscapeDescription':
        return <Landscape_Description layout={layout} specs={specs} />;
      default:
        return null;
    }
  };

  return (
    <div
      className="w-full bg-black relative h-full overflow-y-auto"
      style={{ scrollbarWidth: 'none' }}
      id="landscape-content"
    >
      {/* Hero Section */}
      <Common_HeroSection
        title={detail.title}
        description={detail.description}
        leftTop={[typeTitle, detail.category].filter(Boolean).join(' · ') || detail.organization || ''}
        rightTop={detail.year ? String(detail.year) : detail.prize || ''}
        subtitle={[detail.organization, detail.prize].filter(Boolean).join(' — ')}
        tags={tags}
      />

      {/* An entry with no description block still shows what it knows. */}
      {page.describes ? null : <EntryInformation specs={specs} />}

      {/* Layout Sections */}
      {detail.layout?.map((lay, index) => (
        <GetLayout key={index} layout={lay} />
      ))}
    </div>
  );
}
